import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Gauge, Save, AlertTriangle, Lock } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

const LEVEL_COLORS = {
  1: "#B9262B",
  2: "#E14A1F",
  3: "#F5A623",
  4: "#6B8DB3",
  5: "#22B673",
};

const recomputeMax = (levels) =>
  levels.map((lv, i) => {
    const next = levels[i + 1];
    return { ...lv, max_pct: next ? Number(next.min_pct) - 1 : 100 };
  });

const validate = (levels) => {
  const ordered = [...levels].sort((a, b) => a.level_number - b.level_number);
  for (const lv of ordered) {
    if (!lv.name?.trim()) {
      return { message: `Informe um nome para o Nível ${lv.level_number}.`, badLevels: [lv.level_number] };
    }
  }
  let prevMin = null;
  for (const lv of ordered) {
    const min = lv.level_number === 1 ? 0 : Number(lv.min_pct);
    if (lv.level_number !== 1) {
      if (Number.isNaN(min) || min < 1 || min > 99) {
        return { message: `O piso do Nível ${lv.level_number} deve estar entre 1% e 99%.`, badLevels: [lv.level_number] };
      }
      if (prevMin !== null && min <= prevMin) {
        return {
          message: `O piso do Nível ${lv.level_number} (${min}%) deve ser maior que o piso do Nível ${lv.level_number - 1} (${prevMin}%).`,
          badLevels: [lv.level_number - 1, lv.level_number],
        };
      }
    }
    prevMin = min;
  }
  return null;
};

const AdminMaturity = () => {
  const [levels, setLevels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    api
      .get("/maturity-config")
      .then((r) => setLevels(r.data.levels))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const preview = useMemo(() => recomputeMax(levels), [levels]);
  const validation = useMemo(() => validate(levels), [levels]);
  const error = validation?.message || null;
  const badLevels = validation?.badLevels || [];

  const update = (levelNumber, field, value) => {
    setLevels((prev) =>
      prev.map((lv) => (lv.level_number === levelNumber ? { ...lv, [field]: value } : lv))
    );
  };

  const save = async () => {
    const err = validate(levels)?.message;
    if (err) {
      toast.error(err);
      return;
    }
    setSaving(true);
    try {
      const payload = {
        levels: levels.map((lv) => ({
          level_number: lv.level_number,
          name: lv.name.trim(),
          description: (lv.description || "").trim(),
          min_pct: lv.level_number === 1 ? 0 : Math.round(Number(lv.min_pct)),
        })),
      };
      const r = await api.put("/maturity-config", payload);
      setLevels(r.data.levels);
      toast.success("Parametrização de maturidade atualizada.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-maturity-page">
      <div className="flex items-start justify-between gap-3 mb-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.32em] text-[#6B8DB3]">
            Configuração
          </div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
            <Gauge className="text-[#B9262B]" /> Níveis de Maturidade
          </h1>
          <p className="text-[#9aa3ad] mt-1 max-w-2xl">
            Defina a faixa de % (piso) de cada nível de maturidade. O teto de cada nível é
            calculado automaticamente a partir do piso do próximo nível — isso evita
            sobreposições e lacunas entre as faixas.
          </p>
        </div>
        <Button
          onClick={save}
          disabled={saving || !!error || loading}
          data-testid="save-maturity-config-btn"
          className="bg-[#B9262B] hover:bg-[#9a1e22] text-white shrink-0"
        >
          {saving ? <Loader2 className="animate-spin" /> : (
            <>
              <Save size={14} className="mr-2" /> Salvar
            </>
          )}
        </Button>
      </div>

      {error && (
        <div
          data-testid="maturity-config-error"
          className="mb-6 flex items-start gap-3 p-4 rounded-lg border border-[#B9262B]/40 bg-[#B9262B]/10 text-[#F5A623] text-sm"
        >
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          {error}
        </div>
      )}

      {loading ? (
        <div className="p-10 text-center text-[#6B8DB3]">
          <Loader2 className="animate-spin inline mr-2" /> Carregando...
        </div>
      ) : (
        <div className="space-y-4">
          {preview.map((lv) => (
            <div
              key={lv.level_number}
              data-testid={`maturity-level-card-${lv.level_number}`}
              className={`border rounded-xl p-5 bg-[#0e1015] flex flex-col md:flex-row gap-5 ${
                badLevels.includes(lv.level_number) ? "border-[#B9262B]" : "border-[#3A3F47]"
              }`}
              style={{ borderLeftWidth: 4, borderLeftColor: LEVEL_COLORS[lv.level_number] }}
            >
              <div className="md:w-16 flex md:flex-col items-center md:items-start gap-2 shrink-0">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center font-bold text-white"
                  style={{ backgroundColor: LEVEL_COLORS[lv.level_number] }}
                >
                  {lv.level_number}
                </div>
                <div className="text-[10px] uppercase tracking-wider text-[#6B8DB3]">Nível</div>
              </div>

              <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-[#9aa3ad] text-xs uppercase tracking-wider">Nome</Label>
                  <Input
                    data-testid={`maturity-level-name-${lv.level_number}`}
                    value={lv.name}
                    onChange={(e) => update(lv.level_number, "name", e.target.value)}
                    className="bg-[#1C1F26] border-[#3A3F47] text-white mt-1"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[#9aa3ad] text-xs uppercase tracking-wider flex items-center gap-1">
                      Piso (%) {lv.level_number === 1 && <Lock size={10} />}
                    </Label>
                    {lv.level_number === 1 ? (
                      <div
                        data-testid={`maturity-level-floor-${lv.level_number}`}
                        className="mt-1 h-10 flex items-center px-3 rounded-md border border-[#3A3F47] bg-[#1C1F26]/40 text-[#6B8DB3] text-sm"
                      >
                        0% (fixo)
                      </div>
                    ) : (
                      <Input
                        data-testid={`maturity-level-floor-${lv.level_number}`}
                        type="number"
                        min={1}
                        max={99}
                        value={lv.min_pct}
                        onChange={(e) => update(lv.level_number, "min_pct", e.target.value)}
                        className={`bg-[#1C1F26] text-white mt-1 ${
                          badLevels.includes(lv.level_number) ? "border-[#B9262B]" : "border-[#3A3F47]"
                        }`}
                      />
                    )}
                  </div>
                  <div>
                    <Label className="text-[#9aa3ad] text-xs uppercase tracking-wider">Teto (%)</Label>
                    <div
                      data-testid={`maturity-level-ceil-${lv.level_number}`}
                      className={`mt-1 h-10 flex items-center px-3 rounded-md border text-sm font-mono ${
                        badLevels.includes(lv.level_number)
                          ? "border-[#B9262B]/60 bg-[#B9262B]/10 text-[#B9262B]"
                          : "border-[#3A3F47] bg-[#1C1F26]/40 text-[#22B673]"
                      }`}
                    >
                      {lv.max_pct}% {lv.level_number === 5 && "(fixo)"}
                    </div>
                  </div>
                </div>
                <div className="md:col-span-2">
                  <Label className="text-[#9aa3ad] text-xs uppercase tracking-wider">Descrição</Label>
                  <Textarea
                    rows={2}
                    data-testid={`maturity-level-desc-${lv.level_number}`}
                    value={lv.description || ""}
                    onChange={(e) => update(lv.level_number, "description", e.target.value)}
                    className="bg-[#1C1F26] border-[#3A3F47] text-white mt-1"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminMaturity;
